'use client';

import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductMinQuantity,
  getProductQuantityRules,
  getProductQuantityStep,
  isSellable,
  normalizeProductQuantity,
  normalizeProductQuantityDown,
} from '@eliadmin/core/src/utils/product-utils';
import { useEffect, useMemo } from 'react';

/**
 * Quantity rules of the selected variant (`min_quantity`, `min_quantity_multiple`, packs) and the
 * stock guard of the core `ProductQuantity`: stepping beyond the stock opens the „not in stock“
 * dialog and falls back to the highest sellable quantity.
 */
export function useProductQuantity() {
  const product = useVariantsStore(state => state.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const setSelectedQuantity = useVariantsStore(state => state.setSelectedQuantity);
  const openDialog = useDialogStore(state => state.open);

  const rules = useMemo(
    () => getProductQuantityRules(product, selectedVariant),
    [product, selectedVariant],
  );
  const minQuantity = getProductMinQuantity(rules);
  const step = getProductQuantityStep(rules);
  const stock = selectedVariant ?? product;
  const sellable = isSellable(stock, selectedQuantity);
  const soldOut = stock.quantity < 1 && !sellable;

  const setQuantity = (value: number) => {
    const next = normalizeProductQuantity(value, rules);
    if (isSellable(stock, next)) {
      setSelectedQuantity(next);
    } else if (stock.quantity > 0) {
      openDialog({
        id: NotInStockDialog.id,
        content: <NotInStockDialog maxQuantity={stock.quantity} unit={product.unit ?? ''} />,
      });
      setSelectedQuantity(normalizeProductQuantityDown(stock.quantity, rules));
    }
  };

  useEffect(() => {
    if (stock.quantity <= 0 && stock.availability?.sellable) setSelectedQuantity(minQuantity);
  }, [stock, minQuantity, setSelectedQuantity]);

  useEffect(() => {
    const normalized = normalizeProductQuantity(selectedQuantity, rules);
    if (selectedQuantity !== normalized) setSelectedQuantity(normalized);
  }, [rules, selectedQuantity, setSelectedQuantity]);

  return {
    quantity: selectedQuantity,
    minQuantity,
    step,
    sellable,
    disabled: soldOut,
    setQuantity,
    decrement: () => setQuantity(selectedQuantity - step),
    increment: () => setQuantity(selectedQuantity + step),
  };
}
